import React, { useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ProfileSelector from "../components/ProfileSelector";
import { setCurrentProfile } from "../store/slices/profilesSlice";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  SimCard as SimCardIcon,
  Receipt as ReceiptIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

const Sidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { profiles, currentProfile } = useSelector((state) => state.profiles);

  const handleProfileChange = (profileId) => {
    localStorage.setItem("currentProfile", profileId);
    dispatch(setCurrentProfile(profileId));
  };

  useEffect(() => {
    const savedProfileId = localStorage.getItem("currentProfile");
    if (savedProfileId && profiles.length > 0) {
      dispatch(setCurrentProfile(savedProfileId));
    }
  }, [dispatch, profiles]);

  const menuItems = [
    { title: t("menu.dashboard"), path: "/", icon: DashboardIcon },
    { title: t("menu.purchaseESIM"), path: "/esim", icon: SimCardIcon },
    { title: t("menu.esimOrders"), path: "/orders", icon: SimCardIcon },
    { title: t("menu.transactions"), path: "/transactions", icon: ReceiptIcon },
  ];

  const adminMenuItems = [
    { title: t("menu.adminDashboard"), path: "/admin", icon: AdminIcon },
    { title: t("menu.resellers"), path: "/admin/resellers", icon: GroupIcon },
    {
      title: t("menu.esimOrders"),
      path: "/admin/esim-orders",
      icon: SimCardIcon,
    },
    {
      title: t("menu.transactions"),
      path: "/admin/transactions",
      icon: ReceiptIcon,
    },
    {
      title: t("menu.emailTemplates"),
      path: "/admin/email-templates",
      icon: EmailIcon,
    },
    { title: t("menu.settings"), path: "/admin/settings", icon: SettingsIcon },
  ];

  return (
    <Box sx={{ height: "100%", bgcolor: "background.paper" }}>
      {/* User Info */}
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: "primary.main",
            color: "background.paper",
            width: 40,
            height: 40,
          }}
        >
          {user?.username?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" noWrap>
            {user?.username}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user?.role}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <List component="nav" sx={{ px: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => navigate(item.path)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "& .MuiListItemIcon-root": {
                color: "inherit",
              },
            }}
          >
            <ListItemIcon>
              <item.icon />
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: location.pathname === item.path ? 600 : 400,
              }}
            />
          </ListItem>
        ))}

        {/* Profile section with ProfileSelector */}
        <ListItem
          button
          selected={location.pathname === "/profile"}
          onClick={() => navigate("/profile")}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            "& .MuiListItemIcon-root": {
              color: "inherit",
            },
          }}
        >
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("menu.profile")}
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: location.pathname === "/profile" ? 600 : 400,
            }}
          />
        </ListItem>
        {/* Only show ProfileSelector if profiles exist */}
        {profiles && profiles.length > 1 && (
          <Box sx={{ pl: 6, pr: 2, mb: 2 }}>
            <ProfileSelector
              profiles={profiles}
              currentProfile={currentProfile}
              onProfileChange={handleProfileChange}
            />
          </Box>
        )}

        {/* Admin Menu Section */}
        {user?.role === "admin" && (
          <>
            <Divider sx={{ mt: 2 }} />
            <Typography
              variant="overline"
              sx={{ px: 2, py: 1, display: "block" }}
            >
              {t("menu.adminArea")}
            </Typography>
            <List>
              {adminMenuItems.map((item) => (
                <ListItem
                  button
                  key={item.path}
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    "&.Mui-selected": {
                      bgcolor: "primary.light",
                      "&:hover": {
                        bgcolor: "primary.light",
                      },
                    },
                  }}
                >
                  <ListItemIcon>
                    <item.icon />
                  </ListItemIcon>
                  <ListItemText primary={item.title} />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </List>
    </Box>
  );
};

export default Sidebar;
